import type { InquirySourceState } from './InquirySources/inquirySourceState';
import type { modelState, ModelStateList } from "@/utils/modelState";
import type { SaveProgressInfo } from "./saveProgress";
import type { ProgramBookingState, ReservationProgramBookingDiff, ReservationProgramBookingDiffDto } from "./model.Programs";
import type { RatesRatePlanOverrides } from "./Rates/CustomRatePlan";
import type { RatesBaseRateOverrides } from "./Rates/CustomBaseRate";
import type { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import type { PaymentTypes } from "./PaymentTypes/PaymentType";
import type { RefundTypes } from "./RefundTypes/RefundType";
import type { ReservationStatus } from "./ReservationStatus";
import type { ReservationQuoteStatuses } from "./ReservationQuoteStatuses/ReservationQuoteStatus";
import type { ReservationLogEntry } from "./ReservationLogEntry";
import type { ReservationArrivalTransferMode } from "./ReservationArrivalTransferMode";
import type { ReservationDepartureTransferMode } from "./ReservationDepartureTransferMode";
import type { ReservationLogEntryDto } from "./ReservationLogEntry";
import type { ReservationPersonCache } from "./model.ReservationPerson";
import type { ReservationCompanyCache } from "./model.ReservationCompany";
import type { ReactNode } from 'react';

import type { ExternalPostingStatus, QbJournalLink, ReservationPayment, ReservationPaymentTransaction, ReservationRefund, ReservationRefundTransaction, RevenueItem, ReservationPosItem, ReservationQuote, ReservationNote, ReservationLogEntryContainer, ArrivalTransferDetailsState, DepartureTransferDetailsState, NewPersonDto, NewCompanyDto, PersonDto, CompanyDto, ReservationCompany, ReservationNewCompany, isReservationNewCompany, ReservationExistingCompany, isReservationExistingCompany, ReservationContact, RoomType, RoomFormFieldRefDto, RoomTypeRoom, ProgramType, OccupantType, RoomBlockDatesContainer, RatesContainerDto, RatesRatePlanDto, RatePlanFormFieldDto, RatePlanFormFieldOptionDto, RatePlanFormResponse, RatePlanFieldValue, RatesBaseRateDto, RatesRateMultiplierDto, RatesRateDiscountDto, RatePlanKey, ReservationDiffContainer, RoomSplitInfo, ReservationRoomBookingDiff, ReservationPosItemDiff, DiffTransaction, ReservationPosItemDto, ReservationItemSplitMode, ProgramSplitMode, RoomSplitMode, CheckPriceResult, CheckPriceResultRoom, AvailabilityCheckResultDto, CheckPriceResultProgram, ProgramAvailabilityCheckResultDto, ReservationDiffContainerDto, ReservationRoomBookingDiffDto, ReservationPosItemDiffDto, ReservationLogEntryContainerDto, ReservationRefundDto, ReservationRefundTransactionDto, ReservationPaymentDto, ReservationPaymentTransactionDto, ReservationStatusEnum, VersionHistoryResponse, VersionSummary, VersionRoomSummary, VersionProgramSummary, VersionRoomAvailability, VersionProgramAvailability } from "./types";

/**
 * Props passed to the after-profile slot render function.
 * This allows tenant plugins to inject custom content between Profile and Rooms.
 */
export interface AfterProfileSlotProps {
    /** The reservation ID, or null if creating a new reservation */
    readonly?: boolean;
    reservationId: string | null;
    /** Optional callback when the slot content triggers a save */
    onSave?: () => Promise<void>;
    /** The full reservation state, allowing slots to subscribe to changes */
    reservationState?: ReservationState;
}

/**
 * Render function type for the slot after the profile component.
 * Tenant plugins can provide a render function to inject custom content.
 */
export type AfterProfileSlotRenderFn = (props: AfterProfileSlotProps) => ReactNode;

export interface MacroConfiguration {
    key: string;
    displayName: string;
    component: (props: { reservationState: ReservationState }) => ReactNode;
}

export interface ReservationState {
    InquirySource: InquirySourceState;
    ReservationId: string;
    ReservationNumber: number | null;
    Person: modelState<ReservationPerson>;
    BillingContact?: modelState<ReservationContact | null>;
    Profile: modelState<LoadProfileResult>;
    ArrivalDate: modelState<Date>;
    DepartureDate: modelState<Date>;
    ArrivalDateCalculated: modelState<Date>;
    DepartureDateCalculated: modelState<Date>;
    DatesEnabled: modelState<boolean>;
    /**
     * Reservation-level opt-in override for the `blockPastDateQuoting` tenant-config guard:
     * when true, staff have explicitly allowed quoting/saving against a date before today
     * (Bangkok local) for backfill / historical entry. No role gate - available to all
     * quoting staff. Threaded to the save call as `?allowPastDates=`.
     */
    AllowPastDates: modelState<boolean>;
    RoomBookings: ModelStateList<RoomBookingState>;
    SomeRoomBookingsDirty: modelState<boolean>;
    // Aggregate flag for any room row whose Mandatory rate-option fields are empty.
    // Bubbled up from RoomFormFieldsComponent so SaveReservationComponent can block save without scanning.
    SomeRoomBookingsHaveMissingMandatoryFields: modelState<boolean>;
    addRoomBooking(): void;
    ProgramBookings: ModelStateList<ProgramBookingState>;
    SomeProgramBookingsDirty: modelState<boolean>;
    addProgramBooking(): void;
    Vouchers: Voucher[];
    AppliedVouchers: modelState<SaveReservationRequestVoucher[]>;
    Payments: ModelStateList<ReservationPayment>;
    Refunds: ModelStateList<ReservationRefund>;
    Revenue: RevenueItem[];
    PosItems: modelState<ReservationPosItem[]>;
    AmountPayableTotal: modelState<number>;
    AmountDueTotal: modelState<number>;
    ReservationStatus: modelState<ReservationStatus>;
    ReservationStatusIsDirty: modelState<boolean>;
    ReservationStatusVersion: modelState<number>;
    Quote: modelState<ReservationQuote>;
    QuoteIsDirty: modelState<boolean>;
    QuoteVersion: modelState<number>;
    Notes: ModelStateList<ReservationNote>;
    LogEntries: ModelStateList<ReservationLogEntryContainer>;
    ArrivalTransferDetails: ArrivalTransferDetailsState;
    ArrivalTransferDetailsIsDirty: modelState<boolean>;
    ArrivalTransferDetailsVersion: modelState<number>;
    DepartureTransferDetails: DepartureTransferDetailsState;
    DepartureTransferDetailsIsDirty: modelState<boolean>;
    DepartureTransferDetailsVersion: modelState<number>;
    NewPeople: ModelStateList<NewPersonDto>;
    PersonCache: ReservationPersonCache;
    NewCompanies?: ModelStateList<NewCompanyDto>;
    CompanyCache?: ReservationCompanyCache;
    LoadingError: modelState<string | null>;
    HasCriticalError: modelState<boolean>;
    RoomTypes: { [key: string]: RoomType };
    ProgramTypes: { [key: string]: ProgramType };
    OccupantTypes: { [key: string]: OccupantType };
    RoomBlocks: RoomBlockDatesContainer;
    RevenueLockDate: Date | null;
    /**
     * Override the created UTC timestamp for locked revenue adjustments.
     * When set, this date will be used as the effective date for any revenue
     * changes on dates that fall before the revenue lock date.
     */
    CreatedUtcOverride: modelState<Date | null>;
    Rates: RatesContainerDto;
    SelectedRatePlan: modelState<RatePlanKey | null>;
    RatePlanFormResponseJson: string | null;
    AreAllRoomsAvailable: modelState<boolean>;
    AreAllProgramsAvailable: modelState<boolean>;
    OrderDiffs: modelState<ReservationDiffContainer[]>;
    IsPriceLoading: modelState<boolean>;
    IsDirty: modelState<boolean>;
    save(newStatus?: "Reserved" | "Released" | "Quote" | null): Promise<boolean>;
    IsLoading: modelState<boolean>;
    SaveProgress: modelState<SaveProgressInfo | null>;
    closed?: () => void;
    checkPrices: () => Promise<void>;
    unsubscribeDates: () => void;
    checkRoomsWarning: () => void;
    checkProgramsWarning: () => void;
    RoomsWarningText?: modelState<string>;
    ProgramsWarningText?: modelState<string>
    CanRelease: modelState<boolean>;
    MacroConfigurations: MacroConfiguration[];
    /** Optional render function for tenant-specific content after the profile section */
    renderAfterProfile?: AfterProfileSlotRenderFn;
    /** Optional render function for tenant-specific buttons inside the Tools card */
    renderToolsButtons?: (context?: { readonly: boolean }) => ReactNode;
    MergeableRoomClusters: MergeableRoomCluster[];
}

export interface MergeableRoomCluster {
    roomIds: string[];
    mergedCheckIn: string;
    mergedCheckOut: string;
    roomType: string;
    roomLocationId: string | null;
}

export const ReservationPerson = Symbol("ReservationPerson");
export interface ReservationPerson {
    [ReservationPerson]: boolean;
}

export const ReservationNewPerson = Symbol("ReservationNewPerson");
export interface ReservationNewPerson extends ReservationPerson {
    [ReservationNewPerson]: boolean;
    FirstName: string;
    LastName: string | null;
    Email: string | null;
    Phone: string | null;
}

export function isReservationNewPerson(val: ReservationPerson): val is ReservationNewPerson {
    return val && (val as ReservationNewPerson)[ReservationNewPerson] === true;
}

export const ReservationExistingPerson = Symbol("ReservationExistingPerson");
export interface ReservationExistingPerson extends ReservationPerson {
    [ReservationExistingPerson]: boolean;
    PersonId: string;
    ContactId: string | null;
}

export interface LoadProfileResult {
    Credits: PersonCredit[];
}

export interface PersonCredit {
    CreditId: string;
    Amount: number;
    ExpiresOn: Date;
    SequenceNumber: number;
}

export interface RoomBookingState extends Omit<RoomBooking, 'RoomType' | 'CheckIn' | 'CheckOut' | 'RoomLocationId' | 'Adults' | 'Children' | 'Infants' | 'BaseAmount' | 'PayableAmount' | 'Dates' | 'People' | 'NoShow' | 'NoCharge' | 'Refund' | 'RatePlanOverride' | 'BaseRateOverride' | 'UseOriginalRatePlan' | 'Vouchers' | 'RoomBlocks' | 'RoomMovements' | 'AvailabilityCheckResult' | 'AdditionalCharges' | 'RoomFormResponseJson' | 'OpenPrice' | 'OpenPriceDescription' | 'IgnoreVouchers'> {
    //DataLoader: ReturnType<typeof createRoomBookingDataLoader>;
    RoomType: modelState<string>;
    CheckIn: modelState<Date>;
    CheckOut: modelState<Date>;
    RoomLocationId: modelState<string>;
    Adults: modelState<number>;
    Children: modelState<number>;
    Infants: modelState<number>;
    Price: RoomPrice;
    CalculatedPrice: modelState<CalculatedRowPrice>;
    Dates: RoomBookingDate[];
    People: ModelStateList<RoomBookingPerson>;
    SomePeopleDirty: modelState<boolean>;
    PeopleVersion: modelState<number>;
    NoShow: modelState<boolean>;
    NoShowDirty: modelState<boolean>;
    NoShowVersion: modelState<number>;
    NoCharge: modelState<boolean>;
    NoChargeDirty: modelState<boolean>;
    NoChargeVersion: modelState<number>;
    IsDirty: modelState<boolean>;
    DatesUIVersion: modelState<number>;
    RatePlanOverride: modelState<RatesRatePlanOverrides.RatePlanOverride | null>;
    BaseRateOverride: modelState<RatesBaseRateOverrides.BaseRateOverride | null>;
    RecalculateStayPeriod: modelState<boolean>;
    UseOriginalRatePlan: modelState<boolean | null>;
    AppliedVouchers: modelState<SaveReservationRequestVoucher[]>;
    RoomBlocks: modelState<RoomBlockDate[]>,
    RoomMovements: modelState<number>;
    LockRate: modelState<boolean>;
    Recalculate: modelState<boolean>;
    AvailabilityCheckResult: modelState<AvailabilityCheckResult>;
    AdditionalCharges: modelState<PriceBreakdownContainer[]>;
    RoomFormResponseJson: modelState<string | null>;
    // Display names of Mandatory rate-option fields whose value is empty on this row.
    // Maintained by RoomFormFieldsComponent; empty array = row is valid.
    RoomFormFieldsMissingMandatoryNames: modelState<string[]>;
    OpenPrice: modelState<number | null>;
    OpenPriceDescription: modelState<string | null>;
    IgnoreVouchers: modelState<boolean>;
    // UI-only helper flags (not serialized)
    IsExisting: boolean;
    ForceUnlocked: modelState<boolean>;
}

export interface AvailabilityCheckResult {
    IsAvailable: boolean;
    IsBlockedByTempReservation: boolean;
    BlockedByTempReservationNumbers: number[];
}

// Program availability check result - matches AvailabilityCheckResult structure for consistency
export interface ProgramAvailabilityCheckResult {
    IsAvailable: boolean;
    IsBlockedByTempReservation: boolean;
    BlockedByTempReservationNumbers: number[];
}

export interface RoomBooking {
    ReservationRoomId: string;
    RoomType: string;
    CheckIn: Date;
    CheckOut: Date;
    RoomLocationId: string;
    Adults: number;
    Children: number;
    Infants: number;
    Dates: RoomBookingDateDto[];
    People: RoomBookingPersonDto[];
    NoShow: boolean;
    NoCharge: boolean;
    RatePlanOverride: RatesRatePlanOverrides.RatePlanOverrideDto | null;
    BaseRateOverride: RatesBaseRateOverrides.BaseRateOverrideDto | null;
    UseOriginalRatePlan: boolean | null;
    Vouchers: SaveReservationRequestVoucher[];
    RoomBlocks: RoomBlockDateDto[],
    RoomMovements: number;
    SplitFromReservationRoomId: string | null;
    SplitMode: ReservationItemSplitMode | null;
    AvailabilityCheckResult: AvailabilityCheckResultDto;
    AdditionalCharges: PriceBreakdownContainer[];
    RoomFormResponseJson: string | null;
    OpenPrice: number | null;
    OpenPriceDescription: string | null;
    IgnoreVouchers: boolean;
}

export interface RoomPrice {
    price: modelState<PriceWithDiscount>;
    isLoading: modelState<boolean>;
}

export interface PriceWithDiscount {
    baseAmount: number;
    payableAmount: number | null;
}

export interface RoomBlockDateDto {
    Date: string;
    Rooms: RoomBlockDateRoomDto[];
}

interface RoomBlockDateRoomDto {
    LocationId: string;
    IsAllDay: boolean;
    Start: string;
    End: string;
    ReservationRoomId: string | null;
}

export interface RoomBlockDate {
    Date: Date;
    Rooms: ModelStateList<RoomBlockDateRoom>;
    Version: modelState<number>;
}

export interface RoomBlockDateRoom {
    LocationId: string;
    IsAllDay: boolean;
    Start: Date;
    End: Date;
    ReservationRoomId: string | null;
}

// Per-date computed price: one breakdown for the date plus its rolled-up scalars.
// Discount state is derived where needed via totalBaseAmount > totalPayableAmount.
export interface CalculatedPrice {
    breakdown: PriceBreakdownContainer;
    totalBaseAmount: number;
    totalPayableAmount: number;
}

// Row-level computed price: the row's per-date breakdowns plus the rolled-up scalars.
export interface CalculatedRowPrice {
    breakdowns: PriceBreakdownContainer[];
    totalBaseAmount: number;
    totalPayableAmount: number;
}

export interface RoomBookingDate extends Omit<RoomBookingDateDto, 'Date' | 'SegmentIndex' | 'RatePlanOverride' | 'BaseRateOverride' | 'UseOriginalRatePlan' | 'Vouchers' | 'MinorAdjustment' | 'NoShow' | 'NoCharge' | 'AdditionalCharges' | 'OpenPrice' | 'OpenPriceDescription' | 'IgnoreVouchers'> {
    Date: Date;
    SegmentIndex: modelState<number>;
    RecalculateStayPeriod: modelState<boolean | null>;
    RatePlanOverride: modelState<RatesRatePlanOverrides.RatePlanOverride | null>;
    BaseRateOverride: modelState<RatesBaseRateOverrides.BaseRateOverride | null>;
    UseOriginalRatePlan: modelState<boolean | null>;
    MinorAdjustment: modelState<number>;
    AppliedVouchers: modelState<SaveReservationRequestVoucher[]>;
    NoShow: modelState<boolean>;
    NoCharge: modelState<boolean>;
    AdditionalCharges: modelState<PriceBreakdownContainer[]>;
    OpenPrice: modelState<number | null>;
    OpenPriceDescription: modelState<string | null>;
    IgnoreVouchers: modelState<boolean | null>;
    /**
     * When shifting dates with locked rates, this tells the backend to use the price
     * from this source date instead of recalculating. This allows prices to "shift"
     * with dates when the booking dates change.
     */
    PriceSourceDate?: modelState<Date | null>;
    /**
     * Snapshot-injected evaluation session JSON for this day. When set (with
     * UseOriginalRatePlan = true), the backend persists this as the day's
     * OriginalEvaluationSessionJson and prices the day against it instead of
     * re-evaluating against the live rate plan. Used by the Apply Rate Snapshot
     * flow to overwrite a day's pricing in place without creating a separate
     * BaseRateOverride record.
     */
    EvaluationSessionJson?: modelState<string | null>;
}

export interface RoomBookingDateDto {
    Date: string;
    VoucherEffects: RoomBookingDateVoucher[];
    PriceBreakdown: PriceBreakdownContainer;
    SegmentIndex: number;
    RatePlanOverride: RatesRatePlanOverrides.RatePlanOverrideDto | null;
    BaseRateOverride: RatesBaseRateOverrides.BaseRateOverrideDto | null;
    UseOriginalRatePlan: boolean;
    MinorAdjustment: number;
    CalculatedPrice: CalculatedPrice;
    Vouchers: SaveReservationRequestVoucher[];
    NoShow: boolean;
    NoCharge: boolean;
    AdditionalCharges: PriceBreakdownContainer[];
    OpenPrice: number | null;
    OpenPriceDescription: string | null;
    IgnoreVouchers: boolean | null;
}

export interface RoomBookingDateVoucher {
    Name: string;
    Code: string;
    Amount: number;
    Effects: RoomBookingDateVoucherEffect[];
}

export interface RoomBookingDateVoucherEffect {
    Effect: string;
    Amount: number;
}

export interface PriceBreakdownContainer {
    RatePlanName: string;
    BaseAmount: AmountWithSource;
    Multipliers: MultiplierWithSource[];
    Discounts: MultiplierWithSource[];
    AmountAfterDiscount: SplitAmountsContainer;
    AmountBeforeDiscount: SplitAmountsContainer;
    OpenPriceDescription?: string | null;
}

export interface SplitAmountsContainer {
    Gross: number;
    Vat: number;
    ServiceCharge: number;
    Net: number;
}

export interface AmountWithSource {
    Amount: SplitAmountsContainer;
    Source: string;
}

export interface MultiplierWithSource {
    AdjustmentAmount: SplitAmountsContainer;
    Multiplier: number;
    Source: string;
}

interface RoomBookingPersonDto {
    ReservationRoomPersonId: string;
    PersonId: string;
    CheckIn: string;
    CheckOut: string;
}

export interface RoomBookingPerson {
    ReservationRoomPersonId: string;
    Person: modelState<ReservationPerson>;
    CheckIn: modelState<Date>;
    CheckOut: modelState<Date>;
    IsDirty: modelState<boolean>;
}

export interface Voucher {
    VoucherId: string;
    Name: string;
    Code: string;
    Active: boolean;
}

export interface SaveReservationRequestVoucher {
    reservationVoucherId: string;
    voucherId: string;
    name: string;
    code: string;
}


export * from "./reservationAccountingTypes";
export * from "./reservationDiffTypes";
